import { AbstractDestroyable, Callback1, destroySafe, StoreObserver, VoidCallback } from '@deqo/foundation';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { ObjectManager } from '../../../concepts/objects';
import { TopicManager } from '../../../concepts/objects/TopicManager';
import { TopicSubscription } from '../../../concepts/objects/TopicSubscription';
import { Meeting } from '../../canvas/workflow/Meeting';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { resolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { ToastHub } from '../toast/ToastHub';
import { LiveReadyCheck, LiveReadyCheckState } from './ReadyCheckTypes';
import { ReadyCheckWidget } from './ReadyCheckWidget';

export class ReadyCheckController extends AbstractDestroyable implements LiveReadyCheck {

    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(ProfileModel) private me!: ProfileModel;
    @resolve(ToastHub) private toast!: ToastHub;
    @resolve(TopicManager) private topics!: TopicManager;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    private topic = null as TopicSubscription<LiveReadyCheckState>;

    public get active() {
        return !!this.topic?.getState().active;
    }

    protected async start() {
        this.burden(
            () => destroySafe(this.topic),
            this.objects.createTrigger('ReadyCheck.StopOnMeetingEnd')
                .for(Meeting).includingDead().onDeleted().do(() => this.finish()),
        );
        this.topic = await this.topics.subscribe<LiveReadyCheckState>('readyCheck');
        this.topic.observe(() => this.realizeWidgetIfApplicable());
        this.realizeWidgetIfApplicable();
    }

    public getState(): Readonly<LiveReadyCheckState> {
        return this.topic?.getState() ?? { id: null, active: false };
    }

    public observe(observer: StoreObserver<LiveReadyCheckState>): VoidCallback;
    public observe<P>(path: Callback1<P, LiveReadyCheckState>, observer: StoreObserver<P>): VoidCallback;
    public observe(path: any, observer?: any): VoidCallback {
        return this.topic.observe(path, observer);
    }

    public startNew() {
        this.topic.reset({ id: uuid(), active: true });
    }

    public respondWith(value: boolean, asUserId?: string) {
        asUserId = asUserId ?? this.me.id;
        if (this.active) {
            this.topic.patch({ [asUserId]: value });
            this.toast.send(value ? 'ptpt:ready' : 'ptpt:notReady', {});
        }
    }

    public finish() {
        if (this.active) {
            this.topic.reset({});
        }
    }

    private realizeWidgetIfApplicable = () => {
        this.widgets.delete('readyCheck');

        if (!this.active) return;

        const cont = this.widgets.createContainer({
            name: 'readyCheck',
            position: ['docked', 'topRight', 'inline'],
            style: 'none',
            weight: 200,
        });

        cont.add(this.widgets.createInline({
            name: 'widget',
            ui: ReadyCheckWidget,
            store: this.topic,
        }));

        this.widgets.add(cont);
    };
}
